/**
 * Owner Information Field Names
 *
 * This file contains the canonical field names for owner information
 * across all components in the application. This single source of truth
 * ensures consistent field naming across components.
 */

/**
 * Basic personal information fields available in all owner sections
 */
export const OWNER_INFO_FIELDS = {
  // Basic Identity Fields - present in all forms
  FIRST_NAME: "firstName",
  LAST_NAME: "lastName",
  EMAIL: "email", // NOTE: Not 'personalEmail'
  WORK_EMAIL: "workEmail",

  // Phone Numbers
  MOBILE_PHONE: "mobilePhone",
  MOBILE_PHONE_COUNTRY_CODE: "mobilePhoneCountryCode",
  WORK_PHONE: "workPhone",
  WORK_PHONE_COUNTRY_CODE: "workPhoneCountryCode",
  TRAVEL_PHONE: "travelPhone",
  TRAVEL_PHONE_COUNTRY_CODE: "travelPhoneCountryCode",

  // Messaging
  MESSAGING_CONTACT: "messagingContact",
  MESSAGING_CONTACTS: "messagingContacts",
  MESSAGING_APP: "messagingApp",

  // Toggle State
  IS_ACTIVE: "isActive",
};

/**
 * Extended profile fields used in the Profile page
 * These supplement the basic OWNER_INFO_FIELDS
 */
export const PROFILE_FIELDS = {
  // Basic Profile
  ...OWNER_INFO_FIELDS,

  // System Generated
  FULL_NAME: "fullName",
  NAME: "name", // System field (fullName is used instead)

  // Additional Basic Info
  ALTERNATIVE_EMAIL: "alternativeEmail",
  PHOTO_URL: "photoUrl",
  DATE_OF_BIRTH: "dateOfBirth",
  GENDER: "gender",

  // Address
  ADDRESS: "address",
  ADDRESS_STREET: "address.street",
  ADDRESS_UNIT: "address.unit",
  ADDRESS_CITY: "address.city",
  ADDRESS_STATE: "address.state",
  ADDRESS_ZIP: "address.zip",
  ADDRESS_COUNTRY: "address.country",

  // Emergency Contact
  EMERGENCY_CONTACT: "emergencyContact",
  EMERGENCY_CONTACT_NAME: "emergencyContact.name",
  EMERGENCY_CONTACT_PHONE: "emergencyContact.phone",
  EMERGENCY_CONTACT_RELATIONSHIP: "emergencyContact.relationship",

  // Medical Info
  MEDICAL_INFO: "medicalInfo",
  MEDICAL_INFO_BLOOD_TYPE: "medicalInfo.bloodType",
  MEDICAL_INFO_ALLERGIES: "medicalInfo.allergies",
  MEDICAL_INFO_CONDITIONS: "medicalInfo.conditions",
  MEDICAL_INFO_MEDICATIONS: "medicalInfo.medications",
  MEDICAL_INFO_HEALTH_FUND: "medicalInfo.healthFund",
  MEDICAL_INFO_MEMBER_NUMBER: "medicalInfo.memberNumber",
  MEDICAL_INFO_HEALTH_FUND_ID: "medicalInfo.healthFundId",
  MEDICAL_INFO_CARD_NUMBER: "medicalInfo.cardNumber",
  MEDICAL_INFO_MEDICARE_NUMBER: "medicalInfo.medicareNumber",
  MEDICAL_INFO_MEDICARE_IRN: "medicalInfo.medicareIrn",
  MEDICAL_INFO_MEDICARE_EXPIRY: "medicalInfo.medicareExpiry",
  MEDICAL_INFO_CONCESSION_NUMBER_EXPIRY: "medicalInfo.concessionNumberExpiry",
  MEDICAL_INFO_NDIS_NUMBER: "medicalInfo.ndisNumber",

  // Social Media
  SOCIAL_MEDIA: "socialMedia",
  SOCIAL_MEDIA_INSTAGRAM: "socialMedia.instagram",
  SOCIAL_MEDIA_TIKTOK: "socialMedia.tiktok",
  SOCIAL_MEDIA_FACEBOOK: "socialMedia.facebook",
  SOCIAL_MEDIA_LINKEDIN: "socialMedia.linkedin",
};

/**
 * Standard category-specific fields for ExtremeKit and similar forms
 */
export const CATEGORY_SPECIFIC_FIELDS = {
  // ExtremeKit
  EXTREME_KIT_EQUIPMENT: "categorySpecificInfo.extremeKit.equipment",
  EXTREME_KIT_EMERGENCY_CONTACT:
    "categorySpecificInfo.extremeKit.emergencyContact",
  EXTREME_KIT_MEDICAL_INFO: "categorySpecificInfo.extremeKit.medicalInfo",

  // Tech Info
  TECH_DEVICES: "categorySpecificInfo.tech.devices",

  // Snow Gear
  SNOW_GEAR_SNOWBOARDS: "categorySpecificInfo.snowGear.snowboards",
  SNOW_GEAR_BOOTS: "categorySpecificInfo.snowGear.boots",
  SNOW_GEAR_HELMET_MASK: "categorySpecificInfo.snowGear.helmetMask",
  SNOW_GEAR_GLOVES_APPAREL: "categorySpecificInfo.snowGear.glovesApparel",
};

/**
 * Field names array for easy use in useVCardProfileSync
 */
export const STANDARD_SYNC_FIELDS = [
  OWNER_INFO_FIELDS.FIRST_NAME,
  OWNER_INFO_FIELDS.LAST_NAME,
  OWNER_INFO_FIELDS.EMAIL,
  OWNER_INFO_FIELDS.WORK_EMAIL,
  OWNER_INFO_FIELDS.MOBILE_PHONE,
  OWNER_INFO_FIELDS.MOBILE_PHONE_COUNTRY_CODE,
  OWNER_INFO_FIELDS.WORK_PHONE,
  OWNER_INFO_FIELDS.WORK_PHONE_COUNTRY_CODE,
  OWNER_INFO_FIELDS.TRAVEL_PHONE,
  OWNER_INFO_FIELDS.TRAVEL_PHONE_COUNTRY_CODE,
  OWNER_INFO_FIELDS.MESSAGING_CONTACT,
  OWNER_INFO_FIELDS.MESSAGING_CONTACTS,
  OWNER_INFO_FIELDS.IS_ACTIVE,
];

/**
 * Get sync fields for a specific category
 * @param category The vCard category
 * @returns Array of fields to sync
 */
export const getCategorySyncFields = (category: string): string[] => {
  const baseFields = [...STANDARD_SYNC_FIELDS];

  // Add category-specific fields
  if (category === "extremeKit") {
    baseFields.push(
      CATEGORY_SPECIFIC_FIELDS.EXTREME_KIT_EQUIPMENT,
      CATEGORY_SPECIFIC_FIELDS.EXTREME_KIT_EMERGENCY_CONTACT,
      CATEGORY_SPECIFIC_FIELDS.EXTREME_KIT_MEDICAL_INFO,
    );
  } else if (category === "tech") {
    baseFields.push(CATEGORY_SPECIFIC_FIELDS.TECH_DEVICES);
  } else if (category.startsWith("snowGear_")) {
    // Add relevant snow gear fields based on subcategory
    if (category === "snowGear_snowboards") {
      baseFields.push(CATEGORY_SPECIFIC_FIELDS.SNOW_GEAR_SNOWBOARDS);
    } else if (category === "snowGear_boots") {
      baseFields.push(CATEGORY_SPECIFIC_FIELDS.SNOW_GEAR_BOOTS);
    } else if (category === "snowGear_helmetMask") {
      baseFields.push(CATEGORY_SPECIFIC_FIELDS.SNOW_GEAR_HELMET_MASK);
    } else if (category === "snowGear_glovesApparel") {
      baseFields.push(CATEGORY_SPECIFIC_FIELDS.SNOW_GEAR_GLOVES_APPAREL);
    }
  }

  return baseFields;
};
